import { ReactNode, useEffect, useMemo } from 'react'
import {
  initialQueryState,
  OptionsHelper,
  QueryFilter,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import { useFaturaAtual } from '../../core/useFaturaAtual'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: exportar PDF). */
  actions?: ReactNode
}

export const FaturaAtendimentosListSearchComponent = ({ actions }: Props) => {
  const { t } = useT()
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  // Na visão do responsável da instituição (`:idEmpresa`) só há busca por protocolo.
  const { idEmpresa } = useFaturaAtual()
  const visaoInterna = !idEmpresa
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const queryParams = useMemo(() => {
    const search = [
      debouncedFilters.status ? `ativa:${debouncedFilters.status.value}` : '',
      debouncedFilters.protocolo
        ? `atendimento.protocolo~*${debouncedFilters.protocolo.trim()}*`
        : '',
      debouncedFilters.profissional
        ? `profissional.pessoa.nome~*${debouncedFilters.profissional.trim()}*`
        : '',
    ]
      .filter(Boolean)
      .join(',')

    // Pendência vai como filtro exato (`filters`), não no `search`.
    const queryFilters: QueryFilter[] = debouncedFilters.pendente
      ? [{ field: 'pendente', operator: '=', value: debouncedFilters.pendente.value }]
      : []

    return { search, filters: queryFilters }
  }, [debouncedFilters])

  useEffect(() => {
    updateState({
      ...initialQueryState,
      search: queryParams.search,
      filters: queryParams.filters,
    })
  }, [queryParams, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.protocolo.trim() && {
      key: 'protocolo',
      label: t('protocolo.label'),
      value: `“${filters.protocolo.trim()}”`,
      onRemove: () => setFilter('protocolo', ''),
    },
    filters.profissional.trim() && {
      key: 'profissional',
      label: t('profissional.label'),
      value: `“${filters.profissional.trim()}”`,
      onRemove: () => setFilter('profissional', ''),
    },
    filters.status && {
      key: 'status',
      label: t('situacao.label'),
      value: filters.status.label,
      onRemove: () => setFilter('status', undefined),
    },
    filters.pendente && {
      key: 'pendente',
      label: t('pendente.label'),
      value: filters.pendente.label,
      onRemove: () => setFilter('pendente', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  return (
    <TableFilters
      actions={actions}
      moreFiltersActiveCount={[filters.status, filters.pendente].filter(Boolean).length}
      moreFilters={
        visaoInterna ? (
          <>
            <TableFilter label={t('situacao.label')}>
              <CustomSelect
                entity='situacao'
                onChange={(status?: IOption<boolean>) => setFilter('status', status ?? undefined)}
                options={OptionsHelper.statusCadastro}
                value={filters.status}
              />
            </TableFilter>
            <TableFilter label={t('pendente.label')}>
              <CustomSelect
                placeholder='Selecione'
                onChange={(pendente?: IOption<boolean>) =>
                  setFilter('pendente', pendente ?? undefined)
                }
                options={OptionsHelper.booleanOptions}
                value={filters.pendente}
              />
            </TableFilter>
          </>
        ) : undefined
      }
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'atendimento na fatura' : 'atendimentos na fatura'}`
          }
        />
      }
    >
      <TableFilter label={t('protocolo.label')} wide>
        <Input
          entity='protocolo'
          value={filters.protocolo}
          onChange={(e) => setFilter('protocolo', e.target.value)}
        />
      </TableFilter>
      {visaoInterna && (
        <TableFilter label={t('profissional.label')}>
          <Input
            entity='profissional'
            value={filters.profissional}
            onChange={(e) => setFilter('profissional', e.target.value)}
          />
        </TableFilter>
      )}
    </TableFilters>
  )
}
